// 轻量“按模板硬编码策略”的集中注册与实现（前端版）
// 依赖统一 request(axios) + 动态导入 papaparse/xlsx（用于确保鉴权与自动续期一致）

import { getInventoryUnitDetail } from '../inventory';
import request from '../../utils/request';

export type AutoReviewIssue = {
  level: 'error' | 'warn'
  code: string
  message: string
  locate?: any
}

export type AutoReviewResult = {
  verdict: 'pass' | 'warn' | 'error'
  issues: AutoReviewIssue[]
  suggestedBaselines?: Array<{
    code: string
    format: 'compiled_data' | 'csv' | 'xlsx'
    data: any
  }>
  meta?: any
}

type AttachmentLike = {
  id?: number
  label?: string
  file_name?: string
  file?: string
  file_path?: string
  mime_type?: string
  scope?: string
  meta?: any
}

type TargetLike = any

function normalizeBarcode(input: any): string {
  try {
    const s = String(input || '');
    return s.replace(/\s+/g, '').trim().toUpperCase();
  } catch { return ''; }
}

function getExtLower(name?: string): string {
  try {
    const raw = String(name || '');
    const base = raw.split('?')[0].split('#')[0];
    const m = base.toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
  } catch { return ''; }
}

function bestUrl(a: AttachmentLike): string | undefined {
  return (a?.file || a?.file_path) as string | undefined;
}

function getBackendOrigin(): string {
  try {
    let origin = String((window as any)?.location?.origin || '');
    if (!origin) origin = '';
    // dev: 3000 -> 8000
    try {
      const u = new URL(origin);
      if (u.port === '3000') u.port = '8000';
      origin = u.origin;
    } catch {}
    return origin || '';
  } catch { return ''; }
}

// 统一获取“用于显示/对比”的文件名：优先 file_name → meta.original_name → URL basename
function getBaseName(pathOrUrl?: string): string {
  try {
    const s = String(pathOrUrl || '');
    const q = s.split('?')[0];
    const h = q.split('#')[0];
    const parts = h.split('/');
    const last = parts[parts.length - 1] || s;
    try { return decodeURIComponent(last); } catch { return last; }
  } catch { return String(pathOrUrl || ''); }
}
function prettifyStorageName(name?: string): string {
  const s = String(name || '');
  // 形如: 名称_随机后缀.xlsx → 去掉 _随机后缀
  const m = s.match(/^(.+)_([A-Za-z0-9]{5,12})\.([A-Za-z0-9]+)$/);
  if (m && m[1] && m[3]) return `${m[1]}.${m[3]}`;
  return s;
}
function getDisplayFileName(a: AttachmentLike): string {
  const direct = (a as any)?.file_name;
  if (direct) return String(direct);
  const original = (a as any)?.meta?.original_name || (a as any)?.meta?.original_file_name || (a as any)?.meta?.originalFilename;
  if (original) return String(original);
  const base = getBaseName((a as any)?.file || (a as any)?.file_path);
  return prettifyStorageName(base);
}

async function parseCsv(url: string): Promise<any[][]> {
  let Papa: any = null;
  try {
    // @ts-ignore
    Papa = (await import('papaparse'))?.default || (await import('papaparse'));
  } catch {}
  if (!Papa) throw new Error('缺少 CSV 解析库（papaparse）');
  const resp = await fetch(url, { credentials: 'include' as any });
  const text = await resp.text();
  const parsed = Papa.parse(text, { skipEmptyLines: true });
  const arr = Array.isArray(parsed?.data) ? parsed.data : [];
  return arr;
}

async function parseXlsx(url: string): Promise<any[][]> {
  let XLSX: any = null;
  try {
    // @ts-ignore
    XLSX = await import('xlsx');
  } catch {}
  if (!XLSX) throw new Error('缺少 Excel 解析库（xlsx）');
  const resp = await fetch(url, { credentials: 'include' as any });
  const buf = await resp.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sn = wb.SheetNames?.[0];
  const ws = sn ? wb.Sheets[sn] : null;
  if (!ws) return [];
  const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
  return Array.isArray(aoa) ? aoa : [];
}

async function parseMappingAttachment(att: AttachmentLike): Promise<{ rows: Array<{ rowNo: number, old: any, nw: any, text: any, qty: any, remark: any }> }> {
  const url = bestUrl(att);
  if (!url) throw new Error('映射表无可用链接');
  const ext = getExtLower(att?.file_name || att?.file || att?.file_path || url);
  let aoa: any[][] = [];
  if (ext === 'csv') aoa = await parseCsv(url);
  else aoa = await parseXlsx(url);
  const header = Array.isArray(aoa?.[0]) ? aoa[0] : [];
  const body = aoa.slice(header.length > 0 ? 1 : 0);
  const rows = body.map((r:any[], i:number) => ({
    rowNo: i + 1,
    old: r?.[0],
    nw: r?.[1],
    text: r?.[2],
    qty: r?.[3],
    remark: r?.[4]
  }));
  return { rows };
}

function pickMappingAttachment(atts: AttachmentLike[]): AttachmentLike | null {
  // 优先 label 命中“新旧条码对应表”；否则按扩展名命中 csv/xlsx/xls（取最新）
  const norm = (s?: string) => String(s || '').trim();
  const byLabel = (atts || []).find(a => norm(a.label).includes('新旧条码'));
  if (byLabel) return byLabel;
  const list = (atts || []).filter(a => ['csv','xlsx','xls'].includes(getExtLower(a?.file_name || a?.file || a?.file_path)));
  if (list.length > 0) return list[0];
  return null;
}

function pickPdfAttachments(atts: AttachmentLike[]): AttachmentLike[] {
  return (atts || []).filter(a => {
    const ext = getExtLower(a?.file_name || a?.file || a?.file_path);
    const mime = String(a?.mime_type || '').toLowerCase();
    return ext === 'pdf' || mime.includes('pdf');
  });
}

function pickShippingLabelTargetMappingAttachment(atts: AttachmentLike[]): AttachmentLike | null {
  const norm = (s?: string) => String(s || '').trim();
  const byLabel = (atts || []).find(a => norm(a.label).includes('运输标签与出库对象对应表格'));
  if (byLabel) return byLabel;
  return null;
}

export async function runRegularOutboundShippingMapAuditV1(ctx: {
  targets: TargetLike[]
  attachments: AttachmentLike[]
}): Promise<AutoReviewResult> {
  const issues: AutoReviewIssue[] = [];
  const mappingAtt = pickShippingLabelTargetMappingAttachment(ctx.attachments || []);
  if (!mappingAtt) {
    issues.push({
      level: 'warn',
      code: 'mapping.sltm.attachment_missing',
      message: '未找到“运输标签与出库对象对应表格”附件，未执行自动预审',
    });
    return { verdict: 'warn', issues };
  }

  let rows: Array<{ rowNo: number; old: any; nw: any; text: any; qty: any; remark: any }> = [];
  try {
    const parsed = await parseMappingAttachment(mappingAtt);
    rows = parsed.rows || [];
  } catch (e:any) {
    issues.push({
      level: 'warn',
      code: 'mapping.sltm.parse_failed',
      message: `附件解析失败：${e?.message || e || '未知错误'}`,
    });
    return { verdict: 'warn', issues };
  }

  const toNorm = (v:any) => String(v ?? '').trim();
  const toDupKey = (v:any) => String(v ?? '').trim().toUpperCase();
  const incompleteRows: Array<{ rowNo: number; a: string; b: string; c: string; d: string }> = [];
  const invalidBRows: Array<{ rowNo: number; b: string }> = [];
  const cMap = new Map<string, number[]>();
  const dMap = new Map<string, number[]>();

  (rows || []).forEach((r:any) => {
    const rowNo = Number(r?.rowNo ?? 0) + 1; // 与创建页口径一致：含表头行号
    const a = toNorm(r?.old);
    const b = toNorm(r?.nw);
    const c = toNorm(r?.text);
    const d = toNorm(r?.qty);
    if (!a && !b && !c && !d) return;
    if (!a || !b || (!c && !d)) {
      incompleteRows.push({ rowNo, a, b, c, d });
    }
    if (b && b !== '1') {
      invalidBRows.push({ rowNo, b });
    }
    if (c) {
      const key = toDupKey(c);
      const arr = cMap.get(key) || [];
      arr.push(rowNo);
      cMap.set(key, arr);
    }
    if (d) {
      const key = toDupKey(d);
      const arr = dMap.get(key) || [];
      arr.push(rowNo);
      dMap.set(key, arr);
    }
  });

  const dupC = Array.from(cMap.entries())
    .filter(([, rowNos]) => (rowNos?.length || 0) >= 2)
    .map(([value, rowNos]) => ({ value, count: rowNos.length, rowNos }));
  const dupD = Array.from(dMap.entries())
    .filter(([, rowNos]) => (rowNos?.length || 0) >= 2)
    .map(([value, rowNos]) => ({ value, count: rowNos.length, rowNos }));

  if (incompleteRows.length > 0) {
    issues.push({
      level: 'warn',
      code: 'mapping.sltm.incomplete_row',
      message: `检测到 ${incompleteRows.length} 行记录不完整（A/B 必填，C/D 至少一列有值）`,
      locate: { rows: incompleteRows.slice(0, 100) },
    });
  }
  if (invalidBRows.length > 0) {
    issues.push({
      level: 'warn',
      code: 'mapping.sltm.b_not_one',
      message: `检测到 ${invalidBRows.length} 行 B 列值不为 1`,
      locate: { rows: invalidBRows.slice(0, 100) },
    });
  }
  if (dupC.length > 0) {
    issues.push({
      level: 'warn',
      code: 'mapping.sltm.duplicate_c',
      message: `检测到 ${dupC.length} 组 C 列重复值`,
      locate: { groups: dupC.slice(0, 100) },
    });
  }
  if (dupD.length > 0) {
    issues.push({
      level: 'warn',
      code: 'mapping.sltm.duplicate_d',
      message: `检测到 ${dupD.length} 组 D 列重复值`,
      locate: { groups: dupD.slice(0, 100) },
    });
  }

  const verdict: 'pass' | 'warn' = issues.length > 0 ? 'warn' : 'pass';
  return { verdict, issues, meta: { mapping_rows: rows.length } };
}

export async function runRelabelPackAuditV1(ctx: {
  targets: TargetLike[]
  attachments: AttachmentLike[]
}): Promise<AutoReviewResult> {
  const issues: AutoReviewIssue[] = [];
  const debugEvents: Array<{ tag: string; data: any }> = [];
  const dbg = (tag: string, data: any) => {
    try {
      debugEvents.push({ tag, data });
      // eslint-disable-next-line no-console
      if (debugOn) console.log(`[RelabelAudit][${tag}]`, data);
    } catch {}
  };
  const mappingAtt = pickMappingAttachment(ctx.attachments || []);
  if (!mappingAtt) {
    issues.push({ level: 'error', code: 'mapping.missing', message: '缺少“新旧条码对应表”附件（csv/xlsx）' });
    return { verdict: 'error', issues };
  }
  // 1) 解析映射表
  let rows: Array<{ rowNo:number, old:any, nw:any, text:any, qty:any, remark:any }> = [];
  try {
    const parsed = await parseMappingAttachment(mappingAtt);
    rows = parsed.rows || [];
  } catch (e:any) {
    issues.push({ level: 'error', code: 'mapping.parse_failed', message: `映射表解析失败：${e?.message || e}` });
    return { verdict: 'error', issues };
  }

  // —— 内容审核新增（复用创建指令时的逻辑：去除首尾空格 + 忽略大小写）——
  // 审核1：旧条码=新条码；审核2：重复的 旧→新 组合
  // 注：不改变既有 normalizeBarcode 的行为，避免影响旧逻辑；这里只按“trim + upper”判定
  try {
    const normCode = (v:any) => String(v ?? '').trim().toUpperCase();
    const sameRows: Array<{ rowNo: number; old: string; newer: string }> = [];
    const pairMap: Map<string, Array<{ rowNo: number; old: string; newer: string }>> = new Map();
    (rows || []).forEach((r) => {
      // Create.tsx 的行号习惯为“含表头”，这里额外提供 fileRowNo 以便定位
      const fileRowNo = Number(r?.rowNo ?? 0) + 1;
      const oldN = normCode((r as any)?.old);
      const newN = normCode((r as any)?.nw);
      if (!oldN || !newN) return;
      if (oldN === newN) {
        sameRows.push({ rowNo: fileRowNo, old: oldN, newer: newN });
      }
      const key = `${oldN}→${newN}`;
      const arr = pairMap.get(key) || [];
      arr.push({ rowNo: fileRowNo, old: oldN, newer: newN });
      pairMap.set(key, arr);
    });
    const dupPairs = Array.from(pairMap.entries()).filter(([, arr]) => (arr?.length || 0) >= 2);
    if (sameRows.length > 0) {
      issues.push({
        level: 'error',
        code: 'mapping.old_equals_new',
        message: `检测到 ${sameRows.length} 条记录旧条码与新条码相同（已移除首尾空格，忽略大小写）`,
        locate: { rows: sameRows.slice(0, 50) }
      });
    }
    if (dupPairs.length > 0) {
      const groups = dupPairs.map(([k, arr]) => ({ key: k, count: arr.length, rowNos: arr.map(x => x.rowNo) }));
      issues.push({
        level: 'error',
        code: 'mapping.duplicate_pair',
        message: `检测到 ${dupPairs.length} 组重复的新旧条码对应关系（已移除首尾空格，忽略大小写）`,
        locate: { groups: groups.slice(0, 50) }
      });
    }
  } catch {}
  // Build sets and groups
  const oldSet = new Set<string>();
  const newSet = new Set<string>();
  const oldToLines = new Map<string, Array<typeof rows[0]>>();
  rows.forEach(r => {
    const o = normalizeBarcode(r.old);
    const n = normalizeBarcode(r.nw);
    if (o) {
      oldSet.add(o);
      const arr = oldToLines.get(o) || [];
      arr.push(r);
      oldToLines.set(o, arr);
    }
    if (n) newSet.add(n);
  });
  // 2) 规则2：重复组但未分配数量
  oldToLines.forEach((arr, o) => {
    if (arr.length <= 1) return;
    const bad = arr.some(r => {
      const v = r.qty;
      const num = Number(v);
      return v === undefined || v === null || String(v).trim() === '' || Number.isNaN(num);
    });
    if (bad) {
      issues.push({
        level: 'error',
        code: 'mapping.dup_without_qty',
        message: `旧条码 ${o} 映射多条新条码但存在未指定数量分配的记录`,
        locate: { rows: arr.map(r => r.rowNo) }
      });
    }
  });
  // 3) 规则1：目标条码覆盖
  const extractTargetUnitBarcode = (t:any): string => {
    try {
      const snap = t?.target?.snapshot || t?.snapshot || {};
      return normalizeBarcode(
        snap.system_barcode
        || t?.inventory_unit_system_barcode
        || t?.target?.inventory_unit_system_barcode
        || snap.system_code
        || snap.barcode
        || snap.code
      );
    } catch { return ''; }
  };
  const extractItemBarcodes = (t:any): string[] => {
    try {
      const snap = t?.target?.snapshot || t?.snapshot || {};
      const candidates: any[] = [
        snap?.content?.items,
        t?.content?.items,
        t?.target?.content?.items,
        (snap?.content && Array.isArray(snap.content) ? snap.content : null),
        t?.items,
      ].filter(Boolean);
      for (const cand of candidates) {
        const arr = Array.isArray(cand) ? cand : [];
        if (arr.length > 0) {
          const out: string[] = [];
          arr.forEach((it:any) => {
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
              if (codes.length > 0) {
                codes.forEach((c:string) => {
                  const v = normalizeBarcode(c);
                  if (v) out.push(v);
                });
                return;
              }
            } catch {}
            const fallback = normalizeBarcode(
              it?.product_system_code
              || it?.system_code
              || it?.product_code
              || it?.barcode
              || it?.code
              || ''
            );
            if (fallback) out.push(fallback);
          });
          if (out.length > 0) return out;
        }
      }
      return [];
    } catch { return []; }
  };
  // 调试开关：?logs_debug=1 或 sessionStorage/localStorage.logs_debug=1/true
  const debugOn = (() => {
    try {
      const qs = String((window as any)?.location?.search || '');
      if (/(^|[?&])logs_debug=1(&|$)/.test(qs)) return true;
      const ss = (() => { try { return String((window as any)?.sessionStorage?.getItem?.('logs_debug') || ''); } catch { return ''; } })();
      const ls = (() => { try { return String((window as any)?.localStorage?.getItem?.('logs_debug') || ''); } catch { return ''; } })();
      const yes = new Set(['1','true','yes','on','debug']);
      if (yes.has(ss.toLowerCase())) return true;
      if (yes.has(ls.toLowerCase())) return true;
      return false;
    } catch { return false; }
  })();
  dbg('MappingSets', { oldCount: oldSet.size, newCount: newSet.size, oldSamples: Array.from(oldSet).slice(0, 10) });
  const targets = Array.isArray(ctx.targets) ? ctx.targets : [];

  // 若缺少内容项条码，尝试补拉库存单元详情以提取 product-level 条码
  const unitItemsMap = new Map<string, string[]>();
  try {
    const needFetch: string[] = [];
    (targets || []).forEach((t:any) => {
      const has = (Array.isArray(t?.content?.items) && t.content.items.length > 0)
        || (Array.isArray(t?.target?.content?.items) && t.target.content.items.length > 0)
        || (Array.isArray(t?.snapshot?.content?.items) && t.snapshot.content.items.length > 0)
        || (Array.isArray((t?.target?.snapshot||{})?.content?.items) && (t.target.snapshot as any).content.items.length > 0);
      if (has) return;
      const uid = String(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? '');
      if (uid) needFetch.push(uid);
    });
    const uniq = Array.from(new Set(needFetch));
    if (uniq.length > 0) {
      dbg('IUFetch', { ids: uniq });
      await Promise.all(uniq.map(async (id) => {
        try {
          const resp = await getInventoryUnitDetail(id);
          const d: any = (resp as any)?.data || resp;
          const items: any[] = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
          const out: string[] = [];
          (items || []).forEach((it:any) => {
            try {
              const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
              const codes = sc.map((c:any) => String(c?.code || '')).filter(Boolean);
              if (codes.length > 0) {
                codes.forEach((c:string) => { const v = normalizeBarcode(c); if (v) out.push(v); });
                return;
              }
            } catch {}
            const fallback = normalizeBarcode(
              it?.product_system_code
              || it?.system_code
              || it?.product_code
              || it?.barcode
              || it?.code
              || ''
            );
            if (fallback) out.push(fallback);
          });
          unitItemsMap.set(String(id), Array.from(new Set(out)));
        } catch {
          unitItemsMap.set(String(id), []);
        }
      }));
      dbg('IUFetch[done]', { fetched: uniq.length, mapSize: unitItemsMap.size });
    }
  } catch {}
  const missingCoverage: Array<{ idx: number, barcode: string }> = [];
  targets.forEach((t, idx) => {
    let itemBCs = extractItemBarcodes(t);
    if (itemBCs.length === 0) {
      try {
        const uid = String(t?.target?.inventory_unit_id ?? t?.inventory_unit_id ?? '');
        if (uid && unitItemsMap.has(uid)) {
          const fetched = unitItemsMap.get(uid) || [];
          if (fetched.length > 0) itemBCs = fetched;
        }
      } catch {}
    }
    // 优先以内容物条码作为换标校验对象；若无内容物，则回退到库存单元系统条码
    const barcodes = itemBCs.length > 0 ? itemBCs : [extractTargetUnitBarcode(t)].filter(Boolean);
    dbg('TargetBC', { idx, unitBC: extractTargetUnitBarcode(t), itemCount: itemBCs.length, chosen: barcodes });
    barcodes.forEach(bc => {
      if (bc && !oldSet.has(bc)) missingCoverage.push({ idx, barcode: bc });
    });
  });
  if (missingCoverage.length > 0) {
    issues.push({
      level: 'error',
      code: 'coverage.old_not_contains_targets',
      message: `操作目标中共有 ${missingCoverage.length} 个条码未提供对应的新条码，换标时将无法操作`,
      locate: { missing: missingCoverage.slice(0, 20) }
    });
  }
  // 4) 规则4/5：PDF 与 新条码（文件名命中 + 真实条码解析）
  // 支持：直接 PDF + zip/rar 压缩包内的 PDF 条目（用于“新标签PDF压缩上传”的场景）
  type PdfAuditItem = AttachmentLike & {
    __display_name?: string;              // 用于“文件名=新条码”的比对
    __archive_entry_path?: string;        // 若存在：表示归档内条目PDF
    __archive_source_name?: string;       // 归档文件名（用于展示定位）
  };
  const originForArchive = getBackendOrigin();
  const pdfs: PdfAuditItem[] = [];
  try {
    // 1) 直接 PDF
    pickPdfAttachments(ctx.attachments || []).forEach(p => pdfs.push(p as PdfAuditItem));
    // 2) zip/rar 内 PDF 条目
    const archives = (ctx.attachments || []).filter(a => {
      const ext = getExtLower(a?.file_name || a?.file || a?.file_path);
      return ext === 'zip' || ext === 'rar';
    });
    for (const arc of archives) {
      const arcId = Number((arc as any)?.id);
      if (!Number.isFinite(arcId) || arcId <= 0) continue;
      if (!originForArchive) continue;
      try {
        const listUrl = `${originForArchive}/api/v1/instruction/attachments/${arcId}/archive/list/?t=${Date.now()}`;
        const resp = await fetch(listUrl, { credentials: 'include' as any });
        if (!resp.ok) continue;
        const list = await resp.json();
        const entries: Array<{ path: string; dir: boolean; size?: number }> = Array.isArray(list) ? list : [];
        const arcName = String(getDisplayFileName(arc) || arc?.file_name || '');
        const pdfEntries = entries.filter(e => !!e && !e.dir && String(e.path || '').toLowerCase().endsWith('.pdf'));
        // 安全阈值：避免极端大包导致预审过慢
        const limited = pdfEntries.slice(0, 500);
        limited.forEach((e) => {
          const entryPath = String(e.path || '');
          const base = (() => {
            try {
              const p = entryPath.split('/').pop() || entryPath;
              return p;
            } catch { return entryPath; }
          })();
          pdfs.push({
            ...(arc as any),
            // 关键：仍用归档附件的 id，但携带 entry_path，让解码接口走 archive 分支
            id: arcId,
            __archive_entry_path: entryPath,
            __archive_source_name: arcName,
            __display_name: base,
            // 用于 UI 定位（不影响 normalizeBarcode）
            file_name: `${arcName}::${base}`,
          } as any);
        });
        try { dbg('PDFArchivePick', { archive: arcName || arcId, total: pdfEntries.length, used: limited.length }); } catch {}
      } catch {}
    }
  } catch {}
  try {
    const names = pdfs.map(p => (p?.file_name || p?.file || p?.file_path || '')).slice(0, 10);
    dbg('PDFPick', { count: pdfs.length, samples: names });
  } catch {}
  const pdfNames = pdfs.map(p => {
    const disp = (p as any)?.__display_name || getDisplayFileName(p);
    const name = String(disp || '').replace(/\.pdf$/i, '');
    return normalizeBarcode(name);
  }).filter(Boolean);
  // 5：PDF 必须在新条码列出现
  const pdfNotInNew = pdfNames.filter(n => !newSet.has(n));
  if (pdfNotInNew.length > 0) {
    issues.push({
      level: 'warn',
      code: 'pdf.new_not_in_mapping',
      message: `检测到 ${pdfNotInNew.length} 个新标签PDF的文件名不在“新条码”列，可能为无用文件`,
      locate: { samples: pdfNotInNew.slice(0, 20) }
    });
  }
  // 4：简化版 PDF 审核（只解析第一页 + 模拟打印到 2.25×1.25 再解码）
  // 规则：
  // - 只解析第一页
  // - 模拟打印后：0个条码（或无法解析）=> error；>1个条码 => error；=1个 => 与文件名比对，不一致 => error
  async function decodePdfPage1SimPrint(att: PdfAuditItem): Promise<string[]> {
    try {
      const id = Number((att as any)?.id);
      if (!Number.isFinite(id) || id <= 0) return [];
      const origin = getBackendOrigin();
      if (!origin) return [];
      const entryPath = String((att as any)?.__archive_entry_path || '');
      const isArchiveEntry = !!entryPath;
      const tryBackendDecode = async (params: Record<string, any>): Promise<{ ok: boolean; decoded: string[] }> => {
        const qs = new URLSearchParams();
        qs.set('page', '1');
        qs.set('dpi', '203');
        qs.set('w_in', '2.25');
        qs.set('h_in', '1.25');
        Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && String(v) !== '') qs.set(k, String(v)); });
        if (isArchiveEntry) qs.set('path', entryPath);
        qs.set('t', String(Date.now()));
        const decodeUrl = isArchiveEntry
          ? `${origin}/api/v1/instruction/attachments/${id}/archive/pdf-sim-label-decode/?${qs.toString()}`
          : `${origin}/api/v1/instruction/attachments/${id}/pdf-sim-label-decode/?${qs.toString()}`;
        dbg('PDFSim[BackendDecodeStart]', { id, decodeUrl, isArchiveEntry, entryPath: (isArchiveEntry ? entryPath : undefined) });
        let js: any = null;
        try {
          const resp = await request.get(decodeUrl);
          js = (resp as any)?.data;
        } catch (e: any) {
          const status = Number(e?.response?.status || 0) || undefined;
          dbg('PDFSim[BackendDecodeNotOk]', { id, status, decodeUrl });
          try {
            const ct = String(e?.response?.headers?.['content-type'] || e?.response?.headers?.['Content-Type'] || '');
            const body = e?.response?.data;
            dbg('PDFSim[BackendDecodeBody]', { id, ct, body: String(typeof body === 'string' ? body : JSON.stringify(body || '')).slice(0, 300) });
          } catch {}
          return { ok: false, decoded: [] };
        }
        const decoded = Array.isArray(js?.decoded) ? js.decoded : [];
        dbg('PDFSim[BackendDecoded]', { file: (att?.file_name || ''), decoded, meta: js?.meta });
        return { ok: true, decoded };
      };

      // 优先后端解码（避免前端 ZXing 误判）：依次尝试 cropbox+contain / cropbox+cover
      const attempts: Array<{ tag: string; params: Record<string, any> }> = [
        { tag: 'cropbox+contain', params: { cropbox: 1, fit: 'contain' } },
        { tag: 'cropbox+cover', params: { cropbox: 1, fit: 'cover' } },
      ];
      const decodedUnion: string[] = [];
      let anyOk = false;
      for (const a of attempts) {
        const r = await tryBackendDecode(a.params);
        if (!r.ok) continue;
        anyOk = true;
        const ds = Array.isArray(r.decoded) ? r.decoded : [];
        dbg('PDFSim[BackendAttempt]', { tag: a.tag, file: (att?.file_name || ''), decoded: ds });
        if (ds.length > 0) decodedUnion.push(...ds);
      }
      // 如果后端接口可用但解码为空，则按“不可解码”严格判定；只有当后端接口不可用时才回退空数组
      if (!anyOk) return [];
      const uniq = Array.from(new Set(decodedUnion)).map(normalizeBarcode).filter(Boolean);
      dbg('PDFSim[Decoded]', { file: (att?.file_name || ''), uniq });
      return uniq;
    } catch (e:any) {
      dbg('PDFSim[error]', String(e?.message || e));
      return [];
    }
  }

  // 对每个 PDF 做第一页模拟打印解码，并按严格规则产出错误
  try {
    const decodedPairs = await Promise.all(
      pdfs.map(async (p) => {
        const disp = (p as any)?.__display_name || getDisplayFileName(p);
        const name = String(disp || '').replace(/\.pdf$/i, '');
        const nName = normalizeBarcode(name);
        const decoded = await decodePdfPage1SimPrint(p);
        const arcPrefix = (p as any)?.__archive_source_name ? `${String((p as any).__archive_source_name)}::` : '';
        return { file: `${arcPrefix}${name}`, nFile: nName, decoded };
      })
    );
    const noBarcode = decodedPairs.filter(x => !x || !Array.isArray(x.decoded) || x.decoded.length === 0);
    if (noBarcode.length > 0) {
      issues.push({
        level: 'error',
        code: 'pdf.page1_no_barcode',
        message: `检测到 ${noBarcode.length} 个新标签PDF第一页按2.25×1.25打印模拟后无法解析条码`,
        locate: { samples: noBarcode.slice(0, 20).map(m => ({ file: m.file, decoded: m.decoded || [] })) }
      });
    }
    const multi = decodedPairs.filter(x => Array.isArray(x?.decoded) && x.decoded.length > 1);
    if (multi.length > 0) {
      issues.push({
        level: 'error',
        code: 'pdf.page1_multi_barcode',
        message: `检测到 ${multi.length} 个新标签PDF第一页存在多个可解析条码（不符合“只能包含一个条码”的要求）`,
        locate: { samples: multi.slice(0, 20).map(m => ({ file: m.file, decoded: m.decoded })) }
      });
    }
    const mismatch = decodedPairs.filter(x => Array.isArray(x?.decoded) && x.decoded.length === 1 && x.nFile && !x.decoded.includes(x.nFile));
    if (mismatch.length > 0) {
      issues.push({
        level: 'error',
        code: 'pdf.decoded_filename_mismatch',
        message: `检测到 ${mismatch.length} 个新标签PDF的“文件名”与“解码条码”不一致（第一页按2.25×1.25打印模拟）`,
        locate: { samples: mismatch.slice(0, 20).map(m => ({ file: m.file, decoded: m.decoded })) }
      });
    }
  } catch {}
  // 5) 基线建议（compiled_data）：维持原表结构为主，数量策略由执行端解释
  const compiled = rows.map(r => ({
    A: r.old, B: r.nw, C: r.text, D: r.qty, E: r.remark
  }));
  const suggestedBaselines = [{
    code: 'BARCODE_MAPPING',
    format: 'compiled_data' as const,
    data: { columns: ['A','B','C','D','E'], rows: compiled }
  }];
  // Verdict
  const verdict: 'pass' | 'warn' | 'error' =
    issues.some(i => i.level === 'error') ? 'error' :
    issues.length > 0 ? 'warn' : 'pass';
  return { verdict, issues, suggestedBaselines, meta: { mapping_rows: rows.length, debug_events: debugEvents } };
}

// 注册表（如需扩展更多模板，在此集中映射）
export const StrategyRegistry: Record<string, (ctx:{targets:TargetLike[], attachments:AttachmentLike[]})=>Promise<AutoReviewResult>> = {
  RELABEL_BOX: runRelabelPackAuditV1,
  // 项目实际模板编码（从调试日志得出）：严格匹配系统编码 → 绑定到同一预审函数
  'INSTRUCTIONTEMPLATE-SYS-18': runRelabelPackAuditV1,
  // 常规出库（预审页兜底 key）
  REGULAR_OUTBOUND: runRegularOutboundShippingMapAuditV1,
};

export async function runAutoReviewByTemplateCode(templateCode: string, ctx:{targets:TargetLike[], attachments:AttachmentLike[]}) {
  const key = String(templateCode || '').trim().toUpperCase();
  const fn = StrategyRegistry[key];
  if (!fn) throw new Error(`未找到该模板的自动预审策略：${templateCode}`);
  return fn(ctx);
}


